'use client';

import { useState, useRef } from 'react';
import { useSchoolSchedules, useSchool } from '@/hooks/use-schools';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Plus, Edit, Calendar, Trash2, Copy, Image, Loader2, ChevronDown, FileUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Schedule, CoreSchedule, ModifiedSchedule, SpecialSchedule } from '@/types/api';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { schoolsService } from '@/services/schools.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { getScheduleCode } from '@/lib/schedule-utils';

interface SchoolSchedulesTabProps {
  schoolId: string;
}

export function SchoolSchedulesTab({ schoolId }: SchoolSchedulesTabProps) {
  const { data: schedules, isLoading, error } = useSchoolSchedules(schoolId);
  const { data: school } = useSchool(schoolId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [scheduleToView, setScheduleToView] = useState<Schedule | null>(null);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };

  const deleteMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      await schoolsService.deleteSchedule(schoolId, scheduleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'schedules'] });
      toast.success('Schedule deleted successfully');
      setScheduleToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete schedule');
    },
  });

  // Copy schedule handler
  const handleCopySchedule = (schedule: Schedule) => {
    // Store schedule data in sessionStorage for the create page to pick up
    // Ensure lunch waves have valid slot IDs by remapping to current school's lunch slots
    let lunchWaves = schedule.lunchWaves || [];
    
    if (lunchWaves.length > 0 && school?.lunchSettings?.type === 'waves' && school.lunchSettings.slots) {
      // Remap lunch waves to use valid slot IDs from school settings
      lunchWaves = lunchWaves.map((wave, index) => {
        const slot = school.lunchSettings!.slots![index];
        if (slot) {
          return {
            ...wave,
            slot: { id: slot.id, slot: slot.slot },
          };
        }
        return wave;
      }).filter((_, index) => index < school.lunchSettings!.slots!.length);
    }
    
    const scheduleData = {
      ...schedule,
      id: undefined, // Remove id so it creates a new schedule
      genId: undefined,
      displayName: `${schedule.displayName} (Copy)`,
      createdAt: undefined,
      updatedAt: undefined,
      lunchWaves,
    };
    sessionStorage.setItem('copiedSchedule', JSON.stringify(scheduleData));
    navigate(`/dashboard/schools/${schoolId}/schedules/create?mode=copy`);
  };

  // Image upload handler
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setIsUploading(true);
    
    try {
      toast.info('Uploading and parsing schedule image... This may take up to 20 seconds.');
      
      // Single call: upload to S3 via presigned URL, then call AI endpoint (waits ~20 secs)
      const results = await schoolsService.uploadAndParseScheduleImage(file);

      if (results.length === 0) {
        toast.error('No schedules could be parsed from the image. Please try a clearer image.');
        return;
      }

      // Convert all parsed schedules to form data, passing school data for period type matching
      const allFormData = results.map(r => schoolsService.convertParsedScheduleToFormData(r, schoolId, school || undefined));
      sessionStorage.setItem('parsedSchedules', JSON.stringify(allFormData));
      
      toast.success(`Found ${results.length} schedule${results.length > 1 ? 's' : ''}!`);
      navigate(`/dashboard/schools/${schoolId}/schedules/create?mode=parsed`);
    } catch (error: any) {
      console.error('Failed to parse schedule image:', error);
      toast.error(error?.response?.data?.message || 'Failed to parse schedule image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-36" />
        </div>
        
        {/* Schedule Cards Skeleton */}
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border rounded-lg bg-card">
              <div className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-10 w-10 shrink-0" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-64" />
                </div>
                <Skeleton className="h-9 w-9" />
                <Skeleton className="h-9 w-9" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load schedules. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const schoolPrimaryColor = school?.primaryColor || '#1e40af';

  if (!schedules || schedules.length === 0) {
    return (
      <>
        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-6 mb-4">
              <Calendar className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold">No schedules yet</h3>
            <p className="mt-2 text-sm text-muted-foreground text-center max-w-sm">
              Create your first schedule to define bell times and periods
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  className="mt-6"
                  style={{ 
                    backgroundColor: schoolPrimaryColor,
                    color: getContrastColor(schoolPrimaryColor)
                  }}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Parsing Image...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Schedule
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem onClick={() => navigate(`/dashboard/schools/${schoolId}/schedules/create`)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Manually
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <FileUp className="mr-2 h-4 w-4" />
                  Upload Image (AI)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardContent>
        </Card>
      </>
    );
  }

  // Group schedules by variant
  const coreSchedules = schedules.filter((s): s is CoreSchedule => s.variant === 'core');
  const modifiedSchedules = schedules.filter((s): s is ModifiedSchedule => s.variant === 'modified');
  const specialSchedules = schedules.filter((s): s is SpecialSchedule => s.variant === 'special');

  return (
    <div className="space-y-6">
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Schedules</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {schedules.length} total
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              style={{ 
                backgroundColor: schoolPrimaryColor,
                color: getContrastColor(schoolPrimaryColor)
              }}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Parsing Image...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Schedule
                  <ChevronDown className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate(`/dashboard/schools/${schoolId}/schedules/create`)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Manually
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
              <FileUp className="mr-2 h-4 w-4" />
              Upload Image (AI)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Core Schedules */}
      {coreSchedules.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Core Schedules</h4>
          <div className="grid gap-3">
            {coreSchedules
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((schedule) => (
                <ScheduleCard
                  key={schedule.id}
                  schedule={schedule}
                  onView={() => setScheduleToView(schedule)}
                  onDelete={() => setScheduleToDelete(schedule)}
                  onCopy={() => handleCopySchedule(schedule)}
                  primaryColor={schoolPrimaryColor}
                  schoolId={schoolId}
                />
              ))}
          </div>
        </div>
      )}

      {/* Modified Schedules */}
      {modifiedSchedules.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Modified Schedules</h4>
          <div className="grid gap-3">
            {modifiedSchedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                onView={() => setScheduleToView(schedule)}
                onDelete={() => setScheduleToDelete(schedule)}
                onCopy={() => handleCopySchedule(schedule)}
                primaryColor={schoolPrimaryColor}
                schoolId={schoolId}
              />
            ))}
          </div>
        </div>
      )}

      {/* Special Schedules */}
      {specialSchedules.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Special Schedules</h4>
          <div className="grid gap-3">
            {specialSchedules
              .sort((a, b) => b.date.getTime() - a.date.getTime())
              .map((schedule) => (
                <ScheduleCard
                  key={schedule.id}
                  schedule={schedule}
                  onView={() => setScheduleToView(schedule)}
                  onDelete={() => setScheduleToDelete(schedule)}
                  onCopy={() => handleCopySchedule(schedule)}
                  primaryColor={schoolPrimaryColor}
                  schoolId={schoolId}
                />
              ))}
          </div>
        </div>
      )}

      {/* Schedule Details Modal */}
      {scheduleToView && (
        <ScheduleDetailsDialog
          schedule={scheduleToView}
          open={!!scheduleToView}
          onOpenChange={(open) => !open && setScheduleToView(null)}
          schoolId={schoolId}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!scheduleToDelete} onOpenChange={() => setScheduleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{scheduleToDelete?.displayName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => scheduleToDelete && deleteMutation.mutate(scheduleToDelete.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

interface ScheduleCardProps {
  schedule: Schedule;
  onView: () => void;
  onDelete: () => void;
  onCopy: () => void;
  primaryColor: string;
  schoolId: string;
}

function ScheduleCard({ schedule, onView, onDelete, onCopy, primaryColor, schoolId }: ScheduleCardProps) {

  return (
    <div className="border rounded-lg hover:shadow-md transition-shadow bg-card cursor-pointer" onClick={onView}>
      <div className="flex items-center gap-4 px-4 py-3">
        {/* Schedule Name and Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-base truncate max-w-[300px]" title={schedule.displayName}>
              {schedule.displayName}
            </h4>
            {schedule.isDraft && (
              <Badge variant="destructive" className="text-xs shrink-0">
                Draft
              </Badge>
            )}
            {schedule.variant === 'core' && schedule.isInAcf && (
              <Badge variant="secondary" className="text-xs shrink-0">ACF</Badge>
            )}
            {(schedule.variant === 'core' || schedule.variant === 'modified') && schedule.isInLsc && (
              <Badge variant="outline" className="text-xs shrink-0">LSC</Badge>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-9 w-9"
            onClick={onCopy}
            title="Copy schedule"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-9 w-9"
            onClick={() => window.location.href = `/dashboard/schools/${schoolId}/schedules/${schedule.id}`}
            title="Edit schedule"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={onDelete}
            title="Delete schedule"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function calculateDuration(startTime: string, endTime: string): number {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  return endMinutes - startMinutes;
}

function formatTime(time: string): string {
  const [hour, minute] = time.split(':').map(Number);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
}

interface InfoItemProps {
  label: string;
  value: string;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="text-xs">
        {value}
      </div>
    </div>
  );
}

interface ScheduleDetailsDialogProps {
  schedule: Schedule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
}

function ScheduleDetailsDialog({ schedule, open, onOpenChange, schoolId }: ScheduleDetailsDialogProps) {
  const { data: school } = useSchool(schoolId);
  const schoolPrimaryColor = school?.primaryColor || '#1e40af';
  
  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };
  
  const totalMinutes = schedule.periods.reduce((sum, period) => {
    const duration = calculateDuration(period.startTime, period.endTime);
    return sum + duration;
  }, 0);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Format date and time helpers
  const formatDateTime = (date: Date) => {
    return format(date, 'MMM d, yyyy \'at\' h:mm a');
  };

  const scheduleCode = schedule.originalScheduleCode || getScheduleCode(schedule);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] !max-w-2xl sm:!max-w-2xl">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">{schedule.displayName}</DialogTitle>
            {schedule.isDraft && (
              <Badge variant="destructive" className="ml-2">
                Draft
              </Badge>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)]">
          <div className="py-3 pr-4 space-y-3">
            {/* Metadata Grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              <InfoItem label="Creator" value={schedule.author || 'Unknown'} />
              <InfoItem label="Created on" value={formatDateTime(schedule.createdAt)} />
              <InfoItem label="Schedule code" value={scheduleCode} />
              <InfoItem label="Updated on" value={formatDateTime(schedule.updatedAt)} />
              {schedule.genId && (
                <InfoItem label="Gen ID" value={schedule.genId} />
              )}
              {schedule.targetDate && (
                <InfoItem 
                  label="Target date" 
                  value={format(schedule.targetDate, 'MMMM d, yyyy')}
                />
              )}
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Variant-specific Info Grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {(schedule.variant === 'core' || schedule.variant === 'modified') && (
                <InfoItem label="Day type" value={schedule.dayType.name} />
              )}
              {schedule.variant === 'core' && (
                <InfoItem 
                  label="Day of week" 
                  value={schedule.dayOfWeek || schedule.brokenDayOfWeek || 'N/A'} 
                />
              )}
              {schedule.variant === 'modified' && (
                <InfoItem 
                  label="Modification" 
                  value={
                    schedule.modification === 'OTHER' && schedule.otherModification
                      ? schedule.otherModification
                      : schedule.modification
                  }
                />
              )}
              {schedule.variant === 'special' && (
                <InfoItem 
                  label="Date" 
                  value={format(schedule.date, 'MMMM d, yyyy')}
                />
              )}
              <InfoItem 
                label="Display in LSC" 
                value={
                  schedule.variant !== 'special' && schedule.variant === 'core'
                    ? schedule.isInLsc ? 'Yes' : 'No'
                    : schedule.variant === 'modified' && schedule.isInLsc ? 'Yes' : 'No'
                }
              />
              <InfoItem 
                label="Display in ACF" 
                value={
                  schedule.variant === 'core' && schedule.isInAcf ? 'Yes' : 'No'
                }
              />
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Periods and Lunch Waves */}
            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Periods {schedule.lunchWaves.length > 0 && '& Lunch Waves'}
              </h5>
              <div className="space-y-2">
                {schedule.periods
                  .sort((a, b) => {
                    const [aHour, aMin] = a.startTime.split(':').map(Number);
                    const [bHour, bMin] = b.startTime.split(':').map(Number);
                    return (aHour * 60 + aMin) - (bHour * 60 + bMin);
                  })
                  .map((period, index) => {
                    const duration = calculateDuration(period.startTime, period.endTime);
                    const isRegular = 'dayType' in period && period.dayType;
                    
                    // Find lunch waves that fall within this period's time range
                    const [periodStartHour, periodStartMin] = period.startTime.split(':').map(Number);
                    const [periodEndHour, periodEndMin] = period.endTime.split(':').map(Number);
                    const periodStartMinutes = periodStartHour * 60 + periodStartMin;
                    const periodEndMinutes = periodEndHour * 60 + periodEndMin;
                    
                    const wavesInPeriod = schedule.lunchWaves
                      .filter((wave) => {
                        const [waveStartHour, waveStartMin] = wave.startTime.split(':').map(Number);
                        const waveStartMinutes = waveStartHour * 60 + waveStartMin;
                        return waveStartMinutes >= periodStartMinutes && waveStartMinutes < periodEndMinutes;
                      })
                      .sort((a, b) => {
                        const [aHour, aMin] = a.startTime.split(':').map(Number);
                        const [bHour, bMin] = b.startTime.split(':').map(Number);
                        return (aHour * 60 + aMin) - (bHour * 60 + bMin);
                      });
                    
                    return (
                      <div key={period.id || index}>
                        {/* Period Row */}
                        <div className="flex items-center gap-3 py-2.5 px-3 rounded-md border bg-card text-sm">
                          {isRegular ? (
                            <Badge 
                              className="text-[10px] px-1.5 py-0.5 shrink-0 border-0 font-medium"
                              style={{
                                backgroundColor: schoolPrimaryColor,
                                color: getContrastColor(schoolPrimaryColor)
                              }}
                            >
                              {period.dayType!.name}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0 font-medium">
                              Custom
                            </Badge>
                          )}
                          <span className="font-semibold w-[100px] shrink-0">{period.name}</span>
                          <div className="flex-1 flex items-center justify-center gap-2">
                            <span className="text-muted-foreground font-mono w-[90px] text-right shrink-0">
                              {formatTime(period.startTime)}
                            </span>
                            <span className="text-muted-foreground shrink-0">-</span>
                            <span className="text-muted-foreground font-mono w-[90px] text-left shrink-0">
                              {formatTime(period.endTime)}
                            </span>
                          </div>
                          <Badge variant="secondary" className="font-mono text-xs shrink-0 w-[50px] text-center">
                            {duration}m
                          </Badge>
                        </div>
                        
                        {/* Lunch Waves within this period */}
                        {wavesInPeriod.length > 0 && (
                          <div className="ml-8 mt-1 space-y-1">
                            {wavesInPeriod.map((wave, waveIndex) => {
                              const waveDuration = calculateDuration(wave.startTime, wave.endTime);
                              return (
                                <div
                                  key={wave.id || waveIndex}
                                  className="flex items-center gap-3 py-2 px-3 rounded-md border bg-muted/30 text-sm"
                                >
                                  <div className="flex items-center justify-center shrink-0 text-base px-1">
                                    🍔
                                  </div>
                                  <span className="font-medium w-[100px] shrink-0">Lunch {wave.name}</span>
                                  <div className="flex-1 flex items-center justify-center gap-2">
                                    <span className="text-muted-foreground font-mono w-[90px] text-right shrink-0 text-xs">
                                      {formatTime(wave.startTime)}
                                    </span>
                                    <span className="text-muted-foreground shrink-0">-</span>
                                    <span className="text-muted-foreground font-mono w-[90px] text-left shrink-0 text-xs">
                                      {formatTime(wave.endTime)}
                                    </span>
                                  </div>
                                  <Badge variant="secondary" className="font-mono text-xs shrink-0 w-[50px] text-center">
                                    {waveDuration}m
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t">
          <Button
            style={{ 
              backgroundColor: schoolPrimaryColor,
              color: getContrastColor(schoolPrimaryColor)
            }}
            onClick={() => window.location.href = `/dashboard/schools/${schoolId}/schedules/${schedule.id}`}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
